/**
 * Configuración de las cámaras: golden por grupo y cumplimiento por agencia.
 *
 * La pantalla es **una sola tabla, la de agencias**, y cada agencia se abre
 * para ver sus cámaras. Antes eran dos tablas sueltas —una por agencia y otra
 * con todas las cámaras desviadas— y para saber qué pasaba en Morelia había
 * que buscar Morelia en dos sitios. Con las cámaras colgando de su agencia se
 * abre una, se lee y se cierra.
 *
 * La pregunta que contesta esta pantalla es la que pidió Edgar: **por agencia,
 * cuántas cámaras cumplen el golden y cuántas no**. Con una tercera columna que
 * suele faltar en los tableros y aquí es mayoría: **sin leer**. Una cámara que
 * no se pudo alcanzar ni cumple ni incumple — no se sabe. Mezclarla con las que
 * incumplen es exactamente el error de los 37 rojos falsos del dashboard de
 * Holanda.
 *
 * El golden sale de la mayoría dentro de cada grupo (rol + modelo), pero se
 * muestra el reparto completo a propósito: la mayoría es un candidato, no una
 * verdad. Medido en producción, tres cámaras del mismo modelo y firmware daban
 * H.265 1440p una y H.264 720p las otras dos — y la excepción era la que
 * grababa mejor.
 *
 * Cada cámara fuera de golden trae su botón **Planchar**, que escribe en el
 * equipo detrás del candado `CAMARA_PLANCHADO_APPLY` (el backend responde 409
 * si está apagado). Se plancha de una en una a propósito: el botón de flota
 * hace todas a la vez, y para estrenar el códec se quiere ir por agencia.
 */
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/api/client";
import { ResumenCobertura } from "@/components/ConfigComunes";
import { useCamarasConfig, usePlancharAplicar } from "@/hooks/useConfig";
import { Fragment, useState } from "react";

export function CamarasConfigPanel() {
  const { data, isLoading, isError } = useCamarasConfig();
  const [verGolden, setVerGolden] = useState(false);
  // Que agencias estan abiertas. Se permite mas de una para poder comparar dos,
  // pero se abre ninguna al entrar: la tabla arranca corta.
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const alternar = (nombre: string) =>
    setAbiertas((previas) => {
      const copia = new Set(previas);
      if (copia.has(nombre)) copia.delete(nombre);
      else copia.add(nombre);
      return copia;
    });
  const [filtro, setFiltro] = useState("");
  const plancha = usePlancharAplicar();
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [resultado, setResultado] = useState<Record<string, string>>({});
  const planchar = (id: string) => {
    setConfirmando(null);
    setResultado((r) => ({ ...r, [id]: "planchando…" }));
    plancha.mutate(id, {
      onSuccess: (res) =>
        setResultado((r) => ({
          ...r,
          [id]: res.aplicado ? "✔ planchada" : `no se planchó: ${res.motivo ?? "revisar"}`,
        })),
      onError: (e) =>
        setResultado((r) => ({
          ...r,
          [id]: e instanceof ApiError ? e.message : "no se pudo planchar",
        })),
    });
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Cargando configuración…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-destructive">
        No se pudo consultar la configuración de las cámaras.
      </p>
    );
  }
  if (data._status === "sin_configurar") {
    return (
      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
        {data.mensaje}
      </div>
    );
  }

  const t = data.totales;
  // Sin acentos ni mayusculas: "leon" tiene que encontrar "León".
  const plano = (x: string) =>
    x.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const agencias = (data.agencias ?? []).filter((a) =>
    plano(a.nombre).includes(plano(filtro.trim())),
  );

  return (
    <div className="space-y-4">
      <ResumenCobertura
        totales={{
          total: t?.total ?? t?.camaras ?? 0,
          leidos: t?.leidos ?? t?.leidas ?? 0,
          cobertura_pct: t?.cobertura_pct ?? 0,
          cumplen: t?.cumplen ?? 0,
          revirtieron: t?.revirtieron ?? 0,
          fuera: t?.fuera ?? 0,
          criticos: t?.criticos ?? 0,
          sin_leer: t?.sin_leer ?? 0,
          no_responden: t?.no_responden ?? 0,
        }}
      />

      <p className="text-xs text-muted-foreground">
        Se audita y se plancha sólo la <strong>plantilla estándar</strong>: principal H.265
        (H.264 si el modelo no lo tiene), 1280x720, VBR, calidad media, 6 fps, tope 256 kbps
        e I-frame 50; secundario H.264. Hora y firmware se leen pero no cuentan. Una cámara <em>sin leer</em> no incumple nada — casi siempre es falta de
        ruta a su agencia.{" "}
        <button
          type="button"
          onClick={() => setVerGolden((v) => !v)}
          className="underline underline-offset-2"
        >
          {verGolden ? "ocultar el golden" : "ver el golden por grupo"}
        </button>
      </p>

      {verGolden && (
        <div className="space-y-3">
          {Object.entries(data.golden ?? {}).map(([grupo, campos]) => (
            <div key={grupo}>
              <h4 className="mb-1 text-sm font-medium">{grupo}</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campo</TableHead>
                    <TableHead>Golden</TableHead>
                    <TableHead className="text-right">Cumplen</TableHead>
                    <TableHead className="text-right">Fuera</TableHead>
                    <TableHead>Reparto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(campos).map(([campo, g]) => (
                    <TableRow key={campo}>
                      <TableCell className="font-medium">
                        {campo}
                        {g.critico && (
                          <Badge variant="secondary" className="ml-2">
                            crítico
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs">{g.golden}</code>
                        {g.fijado_a_mano && (
                          <span className="ml-1 text-xs text-muted-foreground">(fijado)</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{g.cumplen}</TableCell>
                      <TableCell
                        className={
                          g.fuera > 0
                            ? "text-right font-medium text-destructive"
                            : "text-right"
                        }
                      >
                        {g.fuera}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {Object.entries(g.reparto)
                          .map(([v, n]) => `${v} (${n})`)
                          .join(" · ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium">Por agencia — toca una para ver sus cámaras</h3>
          <input
            type="search"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar agencia…"
            aria-label="Filtrar agencia"
            className="h-8 w-48 rounded-md border bg-background px-2 text-sm"
          />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agencia</TableHead>
              <TableHead className="text-right">Cumplen</TableHead>
              <TableHead className="text-right">Fuera</TableHead>
              <TableHead className="text-right">Críticos</TableHead>
              <TableHead className="text-right">Sin leer (sin ruta)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agencias.map((a) => {
              const abierta = abiertas.has(a.nombre);
              const suyas = (data.camaras ?? []).filter(
                (c) => (c.agencia ?? "(sin agencia)") === a.nombre,
              );
              const suyasSinLeer = (data.sin_leer ?? []).filter(
                (c) => (c.agencia ?? "(sin agencia)") === a.nombre,
              );
              return (
                <Fragment key={a.nombre}>
                  <TableRow
                    onClick={() => alternar(a.nombre)}
                    className="cursor-pointer"
                    aria-expanded={abierta}
                  >
                    <TableCell className="font-medium">
                      <span className="mr-1 inline-block w-3 text-muted-foreground">
                        {abierta ? "▾" : "▸"}
                      </span>
                      {a.nombre}
                    </TableCell>
                    <TableCell className="text-right">{a.cumplen}</TableCell>
                    <TableCell
                      className={
                        a.fuera > 0 ? "text-right font-medium text-destructive" : "text-right"
                      }
                    >
                      {a.fuera}
                    </TableCell>
                    <TableCell
                      className={
                        a.criticos > 0
                          ? "text-right font-medium text-destructive"
                          : "text-right text-muted-foreground"
                      }
                    >
                      {a.criticos}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {a.sin_leer}
                    </TableCell>
                  </TableRow>

                  {abierta && (
                    <TableRow>
                      <TableCell colSpan={5} className="bg-muted/40 p-0">
                        {/* Tabla propia, con sus encabezados y columnas
                            alineadas: los renglones sueltos no se podian leer
                            en diagonal, que es como se mira una lista de 17
                            camaras buscando la que falla. */}
                        <table className="w-full text-xs">
                          <thead className="text-muted-foreground">
                            <tr className="border-b">
                              <th className="px-3 py-1.5 text-left font-medium">Ubicación</th>
                              <th className="px-3 py-1.5 text-left font-medium">IP</th>
                              <th className="px-3 py-1.5 text-left font-medium">Modelo</th>
                              <th className="px-3 py-1.5 text-left font-medium">Resultado</th>
                              <th className="px-3 py-1.5 text-left font-medium">Detalle</th>
                              <th className="px-3 py-1.5 text-left font-medium" />
                            </tr>
                          </thead>
                          <tbody>
                            {suyas.map((c) => (
                              <tr key={c.device_id} className="border-b last:border-0">
                                <td className="px-3 py-1.5 font-medium">{c.nombre}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{c.ip}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{c.modelo}</td>
                                <td className="px-3 py-1.5">
                                  {c.cumple ? (
                                    <span className="text-muted-foreground">✔ cumple</span>
                                  ) : (
                                    <span className="font-medium text-destructive">
                                      {c.desvios.length} fuera
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5">
                                  {c.desvios.map((d) => (
                                    <div
                                      key={d.campo}
                                      className={d.critico ? "text-destructive" : ""}
                                    >
                                      {d.campo}: <code>{d.actual}</code> →{" "}
                                      <code>{d.esperado}</code>
                                    </div>
                                  ))}
                                </td>
                                <td className="whitespace-nowrap px-3 py-1.5">
                                  {resultado[c.device_id] ? (
                                    <span>{resultado[c.device_id]}</span>
                                  ) : c.cumple ? null : confirmando === c.device_id ? (
                                    <span>
                                      ¿Escribir en la cámara?{" "}
                                      <button
                                        type="button"
                                        className="font-medium text-destructive underline"
                                        onClick={() => planchar(c.device_id)}
                                      >
                                        Sí, planchar
                                      </button>{" "}
                                      <button
                                        type="button"
                                        className="underline"
                                        onClick={() => setConfirmando(null)}
                                      >
                                        No
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="rounded border px-2 py-0.5 font-medium"
                                      onClick={() => setConfirmando(c.device_id)}
                                    >
                                      Planchar
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {suyasSinLeer.map((c) => (
                              <tr key={c.ip} className="border-b opacity-70 last:border-0">
                                <td className="px-3 py-1.5 font-medium">{c.nombre}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{c.ip}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">—</td>
                                {/* Sin leer no es incumplir, y por eso va en la
                                    columna de resultado con esas palabras. */}
                                <td className="px-3 py-1.5 text-muted-foreground">sin leer</td>
                                <td className="px-3 py-1.5 text-muted-foreground">{c.motivo}</td>
                                <td />
                              </tr>
                            ))}
                            {suyas.length === 0 && suyasSinLeer.length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-3 py-2 text-muted-foreground">
                                  Sin cámaras en esta agencia.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
